import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';

const ACCOUNTING_BASE = '/accounting';

/** Normalize URL for segment checks (router may encode slashes in a single segment). */
function normalizeAccountingPath(path: string): string {
  try {
    return decodeURIComponent(path).replace(/\/+/g, '/');
  } catch {
    return path.replace(/\/+/g, '/');
  }
}

/** Full path for guard checks — state.url is preferred; fall back by walking the route tree. */
function resolveGuardPath(route: ActivatedRouteSnapshot, stateUrl: string): string {
  const fromState = normalizeAccountingPath((stateUrl ?? '').split('?')[0]);
  if (fromState.startsWith(ACCOUNTING_BASE)) {
    return fromState;
  }

  const parts: string[] = [];
  let node: ActivatedRouteSnapshot | null = route;
  while (node) {
    const segment = node.routeConfig?.path ?? '';
    if (segment && segment !== '**' && !segment.includes(':')) {
      parts.unshift(segment);
    }
    node = node.parent;
  }

  const joined = parts.filter(Boolean).join('/');
  if (joined.includes('masters') || joined.includes('group')) {
    return normalizeAccountingPath(`${ACCOUNTING_BASE}/${joined}`);
  }

  return fromState;
}

/** Accounting routes are open to all logged-in users (parent authGuard still applies). */
function allowAccountingRoute(path: string, _auth: AuthService): boolean | null {
  const normalized = normalizeAccountingPath(path);
  if (!normalized.startsWith(ACCOUNTING_BASE)) {
    return null;
  }

  return true;

  // const ledgerBase = `${ACCOUNTING_BASE}/ledger`;
  // const vouchersBase = `${ACCOUNTING_BASE}/vouchers`;
  // const dayBookBase = `${ACCOUNTING_BASE}/day-book`;
  // const balanceSheetBase = `${ACCOUNTING_BASE}/balance-sheet`;
  // const canView =
  //   auth.hasViewAccessByPath(ACCOUNTING_BASE) ||
  //   auth.hasViewAccessByPath(ledgerBase) ||
  //   auth.hasViewAccessByPath(vouchersBase) ||
  //   auth.hasViewAccessByPath(dayBookBase) ||
  //   auth.hasViewAccessByPath(balanceSheetBase);
  // const canAdd =
  //   auth.hasAddAccessByPath(ACCOUNTING_BASE) ||
  //   auth.hasAddAccessByPath(ledgerBase) ||
  //   auth.hasAddAccessByPath(vouchersBase) ||
  //   auth.hasAddAccessByPath(dayBookBase) ||
  //   auth.hasAddAccessByPath(balanceSheetBase);
  // const canEdit =
  //   auth.hasEditAccessByPath(ACCOUNTING_BASE) ||
  //   auth.hasEditAccessByPath(ledgerBase) ||
  //   auth.hasEditAccessByPath(vouchersBase) ||
  //   auth.hasEditAccessByPath(dayBookBase) ||
  //   auth.hasEditAccessByPath(balanceSheetBase);
  //
  // if (!canView && !canAdd && !canEdit) {
  //   return false;
  // }
  //
  // if (normalized === ACCOUNTING_BASE || normalized === `${ACCOUNTING_BASE}/`) {
  //   return canView || canAdd || canEdit;
  // }
  //
  // if (normalized.startsWith(`${ACCOUNTING_BASE}/import`)) {
  //   return canView || canAdd || canEdit;
  // }
  //
  // if (
  //   normalized.includes('/masters/create') ||
  //   normalized.endsWith('/group/create') ||
  //   normalized.endsWith('/ledger/create') ||
  //   normalized.includes('/vouchers/create/')
  // ) {
  //   return canAdd || canView;
  // }
  //
  // if (
  //   normalized.includes('/masters/alter') ||
  //   normalized.includes('/group/alter') ||
  //   normalized.includes('/ledger/alter')
  // ) {
  //   if (!auth.hasAccountingAlterAccess()) {
  //     return false;
  //   }
  //   return canView || canAdd || canEdit;
  // }
  //
  // return canView || canAdd || canEdit;
}

/**
 * Guard that allows activation only if the user has view access for the route's path.
 * Uses the full navigation URL (e.g. /accounting/masters/create), not only the lazy-load
 * segment (e.g. /accounting), so nested feature routes resolve the correct module.
 */
export const viewAccessGuard: CanActivateFn = (route, state) => {
  const platformId = inject(PLATFORM_ID);
  const auth = inject(AuthService);
  const router = inject(Router);

  const resolvedPath = resolveGuardPath(route, state.url ?? '');
  const pathsToCheck: string[] = [];
  if (resolvedPath) {
    pathsToCheck.push(resolvedPath);
  }

  const segment = route.routeConfig?.path;
  if (segment) {
    pathsToCheck.push(segment.startsWith('/') ? segment : `/${segment}`);
  }

  if (pathsToCheck.length === 0) {
    return true;
  }

  const accountingAllowed = allowAccountingRoute(resolvedPath, auth);
  if (accountingAllowed !== null) {
    return accountingAllowed ? true : router.createUrlTree(['/dashboard']);
  }

  if (!isPlatformBrowser(platformId)) {
    return pathsToCheck.some((p) => auth.hasViewAccessByPath(p));
  }

  if (!auth.isLoggedIn()) {
    if (auth.hasExpiredSession()) {
      auth.handleSessionExpired();
      return false;
    }
    return router.createUrlTree(['/login']);
  }

  if (pathsToCheck.some((p) => auth.hasViewAccessByPath(p))) {
    return true;
  }

  return router.createUrlTree(['/dashboard']);
};
